/**
 * Round 9 A3 -- a door onto the feed the compliance analyst owns.
 *
 * In a bank the compliance function owns the GRC tool and the feed into
 * it. In the product she was refused /admin/service-accounts, civilly,
 * and eight screens she reaches daily contained no occurrence of
 * "service account", "access key", "GRC", "pull", "export", "outbound"
 * or "scope". The bank's own outbound contract was invisible to the
 * person accountable for what goes out of it.
 *
 * Read-only on purpose. Minting a credential is an administrator's act
 * and stays where it is; what was missing is any way for the owner of
 * the integration to see what it exposes, at what grain, and how a
 * consumer walks it -- so she can brief her GRC vendor and ask for a key
 * knowing what she is asking for.
 */
import { useTranslation } from "react-i18next";

import { SCOPE_ROUTES } from "shared/admin/OutboundContract";
import { MarsadIcon } from "shared/components/MarsadIcon";
import { SERVICE_SCOPES } from "shared/api/types";

export function OutboundFeedView() {
  const { t } = useTranslation();
  const base =
    typeof window === "undefined" ? "https://your-marsad-host" : window.location.origin;
  const baseUrl = base + "/v1/ext";

  return (
    <article data-testid="outbound-feed-view" className="space-y-6">
      <header>
        <h1 className="flex items-center gap-2 font-display text-2xl text-brand-navy">
          <MarsadIcon name="verifier" className="h-6 w-6 text-brand-brass" />
          {t("screens.outboundFeed.heading")}
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600">
          {t("screens.outboundFeed.intro")}
        </p>
      </header>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-brand-navy">
          {t("screens.outboundFeed.whatItExposes")}
        </h2>
        <table className="mt-3 w-full text-sm">
          <thead>
            <tr className="border-b border-slate-200 text-start text-xs uppercase tracking-wide text-slate-500">
              <th className="py-2 pe-3 text-start">
                {t("screens.outboundFeed.scope")}
              </th>
              <th className="py-2 pe-3 text-start">
                {t("screens.outboundFeed.route")}
              </th>
              <th className="py-2 text-start">{t("screens.outboundFeed.grain")}</th>
            </tr>
          </thead>
          <tbody data-testid="outbound-feed-scopes">
            {SERVICE_SCOPES.map((scope) => {
              const row = SCOPE_ROUTES[scope];
              return (
                <tr key={scope} className="border-b border-slate-100 align-top">
                  <td className="py-2 pe-3 font-mono text-xs text-brand-navy">
                    {scope}
                  </td>
                  <td className="py-2 pe-3 font-mono text-xs text-slate-700">
                    {row ? row.route : "--"}
                  </td>
                  <td className="py-2 text-xs text-slate-600">
                    {row ? row.note : "--"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="mt-3 text-xs text-slate-500" data-testid="outbound-feed-base">
          {t("screens.outboundFeed.baseUrl")} <code>{baseUrl}</code>
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-brand-navy">
          {t("screens.outboundFeed.howAConsumerWalksIt")}
        </h2>
        <p className="mt-2 max-w-3xl text-sm text-slate-600">
          {t("screens.outboundFeed.cursor")}
        </p>
      </section>

      <section className="rounded-lg border border-brand-brass/40 bg-brand-brass/5 p-4">
        <h2 className="text-sm font-semibold text-brand-navy">
          {t("screens.outboundFeed.isItWatched")}
        </h2>
        {/* The answer was "no" until round 9 A1, which is why this
            section exists rather than being assumed. */}
        <p className="mt-2 max-w-3xl text-sm text-slate-700">
          {t("screens.outboundFeed.audited")}
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="text-sm font-semibold text-brand-navy">
          {t("screens.outboundFeed.gettingAKey")}
        </h2>
        <p
          className="mt-2 max-w-3xl text-sm text-slate-600"
          data-testid="outbound-feed-request"
        >
          {t("screens.outboundFeed.requestFromAdmin")}
        </p>
      </section>
    </article>
  );
}
